export interface BloodCrimesAudioAnalysis {
  sha256: string;
  sourceRefHash: string;
  mimeType: string;
  sourceBytes: number;
  durationSeconds: number;
  sampleRate: number;
  channelCount: number;
  peakDbfs: number;
  rmsDbfs: number;
  quietThresholdDbfs: -50;
  quietMinimumSeconds: 1;
  quietSectionCount: number;
  quietSeconds: number;
  method: 'BROWSER_WEBAUDIO';
}

interface DecodedAudioLike {
  duration: number;
  sampleRate: number;
  numberOfChannels: number;
  length: number;
  getChannelData(channel: number): Float32Array;
}

const MAX_SOURCE_BYTES = 25 * 1024 * 1024;
const QUIET_THRESHOLD = 10 ** (-50 / 20);
const DB_FLOOR = -120;

function toDbfs(amplitude: number): number {
  return amplitude <= 10 ** (DB_FLOOR / 20) ? DB_FLOOR : Math.max(DB_FLOOR, 20 * Math.log10(amplitude));
}

export function analyzeDecodedBloodCrimesAudio(audio: DecodedAudioLike, bytes: ArrayBuffer, mimeType: string, sourceRefHash: string): Omit<BloodCrimesAudioAnalysis, 'sha256'> {
  if (!Number.isFinite(audio.duration) || audio.duration <= 0 || audio.duration > 7200) throw new Error('Utwór musi mieć od 0 do 7200 sekund.');
  if (!Number.isInteger(audio.sampleRate) || audio.sampleRate < 8_000 || audio.sampleRate > 384_000) throw new Error('Nieobsługiwana częstotliwość próbkowania.');
  if (!Number.isInteger(audio.numberOfChannels) || audio.numberOfChannels < 1 || audio.numberOfChannels > 8 || audio.length < 1) throw new Error('Nieobsługiwany układ kanałów audio.');
  if (bytes.byteLength < 1 || bytes.byteLength > MAX_SOURCE_BYTES) throw new Error('Plik audio przekracza limit 25 MB.');

  const channels = Array.from({ length: audio.numberOfChannels }, (_, channel) => audio.getChannelData(channel));
  const windowFrames = Math.max(1, Math.floor(audio.sampleRate / 10));
  let sumSquares = 0;
  let sampleCount = 0;
  let peak = 0;
  let quietRun = 0;
  let quietSectionCount = 0;
  let quietSeconds = 0;
  let currentQuietSeconds = 0;

  for (let start = 0; start < audio.length; start += windowFrames) {
    const end = Math.min(audio.length, start + windowFrames);
    let windowSquares = 0;
    let windowCount = 0;
    for (let frame = start; frame < end; frame += 1) {
      for (const channel of channels) {
        const sample = channel[frame] ?? 0;
        const amplitude = Math.abs(sample);
        if (amplitude > peak) peak = amplitude;
        sumSquares += sample * sample;
        windowSquares += sample * sample;
        sampleCount += 1;
        windowCount += 1;
      }
    }
    const windowRms = Math.sqrt(windowSquares / Math.max(1, windowCount));
    const seconds = (end - start) / audio.sampleRate;
    if (windowRms < QUIET_THRESHOLD) {
      quietRun += 1;
      currentQuietSeconds += seconds;
    } else {
      if (quietRun >= 10) {
        quietSectionCount += 1;
        quietSeconds += currentQuietSeconds;
      }
      quietRun = 0;
      currentQuietSeconds = 0;
    }
  }
  if (quietRun >= 10) {
    quietSectionCount += 1;
    quietSeconds += currentQuietSeconds;
  }

  return {
    sourceRefHash,
    mimeType: mimeType.slice(0, 80),
    sourceBytes: bytes.byteLength,
    durationSeconds: Number(audio.duration.toFixed(3)),
    sampleRate: audio.sampleRate,
    channelCount: audio.numberOfChannels,
    peakDbfs: Number(toDbfs(peak).toFixed(2)),
    rmsDbfs: Number(toDbfs(Math.sqrt(sumSquares / Math.max(1, sampleCount))).toFixed(2)),
    quietThresholdDbfs: -50,
    quietMinimumSeconds: 1,
    quietSectionCount,
    quietSeconds: Number(quietSeconds.toFixed(2)),
    method: 'BROWSER_WEBAUDIO',
  };
}

async function sha256(bytes: BufferSource): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function readCappedBody(response: Response): Promise<ArrayBuffer> {
  const declaredLength = Number(response.headers.get('content-length'));
  if (Number.isFinite(declaredLength) && declaredLength > MAX_SOURCE_BYTES) throw new Error('Plik audio przekracza limit 25 MB.');
  if (!response.body) {
    const bytes = await response.arrayBuffer();
    if (bytes.byteLength > MAX_SOURCE_BYTES) throw new Error('Plik audio przekracza limit 25 MB.');
    return bytes;
  }
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let size = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (!value) continue;
      size += value.byteLength;
      if (size > MAX_SOURCE_BYTES) {
        await reader.cancel();
        throw new Error('Plik audio przekracza limit 25 MB.');
      }
      chunks.push(value);
    }
  } finally {
    reader.releaseLock();
  }
  const joined = new Uint8Array(size);
  let offset = 0;
  for (const chunk of chunks) {
    joined.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return joined.buffer;
}

export async function analyzeBloodCrimesAudioUrl(sourceRef: string): Promise<BloodCrimesAudioAnalysis> {
  let url: URL;
  try { url = new URL(sourceRef); } catch { throw new Error('Referencja utworu nie jest prawidłowym URL-em.'); }
  if (url.protocol !== 'https:' || url.username || url.password) throw new Error('Analiza wymaga bezpiecznego publicznego adresu HTTPS.');

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 60_000);
  let context: AudioContext | null = null;
  try {
    const response = await fetch(url.toString(), { mode: 'cors', credentials: 'omit', signal: controller.signal });
    if (!response.ok) throw new Error(`Nie udało się pobrać utworu (HTTP ${response.status}).`);
    const mimeType = response.headers.get('content-type')?.split(';')[0]?.trim().toLowerCase() || 'application/octet-stream';
    if (!mimeType.startsWith('audio/') && mimeType !== 'application/octet-stream') throw new Error('Źródło nie zwróciło pliku audio.');
    const bytes = await readCappedBody(response);
    if (!bytes.byteLength) throw new Error('Plik audio jest pusty.');
    const sourceRefHash = await sha256(new TextEncoder().encode(url.toString()));
    const digest = await sha256(bytes.slice(0));
    const AudioContextConstructor = window.AudioContext;
    if (!AudioContextConstructor) throw new Error('Ta przeglądarka nie obsługuje Web Audio API.');
    context = new AudioContextConstructor();
    const decoded = await context.decodeAudioData(bytes.slice(0));
    return { ...analyzeDecodedBloodCrimesAudio(decoded, bytes, mimeType, sourceRefHash), sha256: digest };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw new Error('Analiza przekroczyła limit 60 sekund.');
    if (error instanceof TypeError) throw new Error('Nie można pobrać pliku. Sprawdź dostępność URL i CORS.');
    throw error;
  } finally {
    window.clearTimeout(timeout);
    if (context && context.state !== 'closed') await context.close().catch(() => undefined);
  }
}

export function bloodCrimesAudioSourceRefHash(sourceRef: string): Promise<string> {
  return sha256(new TextEncoder().encode(new URL(sourceRef).toString()));
}
